import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Megaphone, Pause, Play, Plus, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import {
  ApiError,
  cancelCampaign,
  fetchCampaigns,
  pauseCampaign,
  resumeCampaign,
  startCampaign,
  type CampaignSummaryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * CRM → Campanhas (F054): "pegar todos os clientes que pegam produto tal e
 * mandar msg falando que vai ter reajuste de preço" — a palavra do dono.
 *
 * Esta tela é a LISTA e o acompanhamento. A criação virou assistente em
 * página (`/crm-campanhas/nova`, F060) — o modal de nove campos que morava
 * aqui pedia conta, nome, segmento, produto, duas datas, modelo e ritmo numa
 * caixa só, com uma contagem opcional que **não era invalidada quando o
 * segmento mudava**.
 *
 * ## Iniciar e cancelar passam a perguntar
 *
 * O ▶ disparava mensagens PAGAS com um clique, e o ✕ cancelava uma campanha
 * em andamento sem nenhuma pergunta. Os dois agora passam por
 * `ConfirmDialog`, com o número de destinatários na frase quando ele existe.
 * Pausar e retomar não perguntam de propósito: são reversíveis e não gastam
 * nada.
 */

const STATUS_LABEL: Record<CampaignSummaryType['status'], string> = {
  draft: 'Rascunho',
  scheduled: 'Agendada',
  running: 'Em andamento',
  paused: 'Pausada',
  done: 'Concluída',
  cancelled: 'Cancelada',
}

const STATUS_TONE: Record<
  CampaignSummaryType['status'],
  'neutral' | 'success' | 'warning' | 'danger'
> = {
  draft: 'neutral',
  scheduled: 'warning',
  running: 'success',
  paused: 'warning',
  done: 'neutral',
  cancelled: 'danger',
}

/**
 * O número na PERGUNTA, não só no aviso genérico. `totalRecipients` só é
 * preenchido depois que o worker materializa a lista, então uma campanha em
 * rascunho ainda não sabe quantos são — e a frase diz isso em vez de mentir
 * um zero.
 */
const recipientsPhrase = (campaign: CampaignSummaryType): string =>
  campaign.totalRecipients > 0
    ? `Enviar para ${String(campaign.totalRecipients)} pessoas?`
    : `Iniciar "${campaign.name}" agora?`

const dateLabel = (iso: string | null): string => {
  if (iso === null) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const CrmCampanhasPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  const canManage = allows('communication.campaign.manage')

  /** A transição que espera confirmação — `null` quando nenhuma está pendente. */
  const [confirming, setConfirming] = useState<{
    campaign: CampaignSummaryType
    action: 'start' | 'cancel'
  } | null>(null)

  const campaigns = useQuery({
    queryKey: ['communication-campaigns'],
    queryFn: async ({ signal }) => await fetchCampaigns(signal),
  })

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['communication-campaigns'],
    })
  }

  const [transitionError, setTransitionError] = useState<string | null>(null)

  const transition = useMutation({
    mutationFn: async (input: {
      id: string
      version: number
      action: 'start' | 'pause' | 'resume' | 'cancel'
    }) => {
      const body = { version: input.version }
      if (input.action === 'start') await startCampaign(input.id, body)
      else if (input.action === 'pause') await pauseCampaign(input.id, body)
      else if (input.action === 'resume') await resumeCampaign(input.id, body)
      else await cancelCampaign(input.id, body)
    },
    onSuccess: async () => {
      setTransitionError(null)
      await invalidate()
    },
    // Falha dita, e não engolida: "Pausar" que não pausa e não avisa deixa a
    // campanha mandando mensagem com a pessoa achando que parou.
    onError: (cause: unknown) => {
      setTransitionError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível mudar a campanha. Tente de novo.'
      )
    },
  })

  /** Só a linha clicada gira; as outras continuam clicáveis. */
  const busy = (id: string): boolean =>
    transition.isPending && transition.variables.id === id

  const rows = campaigns.data ?? []

  return (
    <>
      <TabHeader
        title="Campanhas"
        description='"Pegar todos os clientes que compraram X e avisar de uma vez" — segmento, ritmo (compartilhado com o atendimento ao vivo) e acompanhamento.'
        actions={
          canManage ? (
            <Button asChild>
              <Link to="/crm-campanhas/nova">
                <Plus aria-hidden="true" /> Nova campanha
              </Link>
            </Button>
          ) : null
        }
      />

      {transitionError === null ? null : (
        <InlineAlert tone="danger">{transitionError}</InlineAlert>
      )}

      {campaigns.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : campaigns.isError ? (
        <EmptyState
          icon={<Megaphone aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar as campanhas"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Megaphone aria-hidden="true" className="h-8 w-8" />}
          title="Nenhuma campanha"
          description={
            canManage
              ? 'Crie uma campanha para avisar um grupo de clientes de uma vez.'
              : 'Peça a um administrador para criar uma campanha.'
          }
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Campanha</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                <TableHeaderCell>Ritmo</TableHeaderCell>
                <TableHeaderCell>Destinatários</TableHeaderCell>
                <TableHeaderCell>Enviadas</TableHeaderCell>
                <TableHeaderCell>Puladas</TableHeaderCell>
                <TableHeaderCell>Falharam</TableHeaderCell>
                <TableHeaderCell>Agendada para</TableHeaderCell>
                {canManage ? <TableHeaderCell>&nbsp;</TableHeaderCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>
                    <Badge tone={STATUS_TONE[campaign.status]}>
                      {STATUS_LABEL[campaign.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>{campaign.throttleMps} mps</TableCell>
                  <TableCell>{campaign.totalRecipients}</TableCell>
                  <TableCell>{campaign.sentCount}</TableCell>
                  <TableCell>{campaign.skippedCount}</TableCell>
                  <TableCell>{campaign.failedCount}</TableCell>
                  <TableCell>{dateLabel(campaign.scheduledFor)}</TableCell>
                  {canManage ? (
                    <TableCell>
                      <div className="flex gap-1">
                        {campaign.status === 'draft' ||
                        campaign.status === 'scheduled' ? (
                          <Button
                            variant="ghost"
                            title="Iniciar agora"
                            loading={busy(campaign.id)}
                            onClick={() => {
                              setConfirming({ campaign, action: 'start' })
                            }}
                          >
                            <Play aria-hidden="true" className="h-4 w-4" />
                          </Button>
                        ) : null}
                        {campaign.status === 'running' ? (
                          <Button
                            variant="ghost"
                            title="Pausar"
                            loading={busy(campaign.id)}
                            onClick={() => {
                              transition.mutate({
                                id: campaign.id,
                                version: campaign.version,
                                action: 'pause',
                              })
                            }}
                          >
                            <Pause aria-hidden="true" className="h-4 w-4" />
                          </Button>
                        ) : null}
                        {campaign.status === 'paused' ? (
                          <Button
                            variant="ghost"
                            title="Retomar"
                            loading={busy(campaign.id)}
                            onClick={() => {
                              transition.mutate({
                                id: campaign.id,
                                version: campaign.version,
                                action: 'resume',
                              })
                            }}
                          >
                            <Play aria-hidden="true" className="h-4 w-4" />
                          </Button>
                        ) : null}
                        {campaign.status !== 'done' &&
                        campaign.status !== 'cancelled' ? (
                          <Button
                            variant="ghost"
                            title="Cancelar"
                            loading={busy(campaign.id)}
                            onClick={() => {
                              setConfirming({ campaign, action: 'cancel' })
                            }}
                          >
                            <X aria-hidden="true" className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null)
        }}
        tone={confirming?.action === 'cancel' ? 'danger' : 'neutral'}
        title={
          confirming === null
            ? ''
            : confirming.action === 'start'
              ? recipientsPhrase(confirming.campaign)
              : `Cancelar "${confirming.campaign.name}"?`
        }
        description={
          confirming?.action === 'start'
            ? 'Cada mensagem enviada é cobrada pela Meta. Depois de iniciada, a campanha pode ser pausada, mas o que já saiu não volta.'
            : 'Quem ainda não recebeu não vai receber. O que já saiu não volta, e a campanha não pode ser retomada depois.'
        }
        confirmLabel={
          confirming?.action === 'start' ? 'Iniciar' : 'Cancelar campanha'
        }
        cancelLabel="Voltar"
        loading={transition.isPending}
        onConfirm={() => {
          const pending = confirming
          setConfirming(null)
          if (pending !== null) {
            transition.mutate({
              id: pending.campaign.id,
              version: pending.campaign.version,
              action: pending.action,
            })
          }
        }}
      />
    </>
  )
}
